// /en/dtv/$category/for/$nationality — Triple-key EN mirror (DTV × nationality)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { DTV_CATEGORIES } from "@/data/dtv-categories";
import { VISA_NATIONALITIES } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { isIndexable } from "@/data/indexable-whitelist";
import { ContactCTA, FaqAccordion, DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, ShieldCheck, BadgeCheck, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/dtv/$category/for/$nationality")({
  loader: async ({ params }) => {
    const [{ DTV_CATEGORIES: LIST }, { VISA_NATIONALITIES: NATS }] = await Promise.all([
      import("@/data/dtv-categories"),
      import("@/data/visa-nationalities"),
    ]);
    const cat = LIST.find((x) => x.slug === params.category);
    const nat = NATS.find((n) => n.slug === params.nationality);
    if (!cat || !nat) throw notFound();
    return { cat, nat };
  },
  head: ({ loaderData }) => {
    const cat = loaderData?.cat;
    const nat = loaderData?.nat;
    if (!cat || !nat) return { meta: [{ title: ctaTitle("Not found", "/en/dtv/$category/for/$nationality", "en")}] };
    const url = `${SITE}/en/dtv/${cat.slug}/for/${nat.slug}`;
    const title = `${cat.titleEn} for ${nat.demonymEn} Citizens — Process, Documents & Fees 2026 | NYC`;
    const desc = `Apply for ${cat.titleEn} as a citizen of ${nat.countryEn} — ${nat.visaExempt ? `${nat.visaExempt}-day visa exempt entry` : "pre-arrival visa required"}. Govt fee THB 10,000 + NYC service THB ${cat.servicePriceTHB.toLocaleString()}, processed in ${cat.processingDays}.`;
    const faqs = [
      { q: `Can ${nat.demonymEn} citizens apply for ${cat.titleEn}?`, a: `Yes — ${cat.titleEn} is open to all nationalities including ${nat.countryEn}. File via thaievisa.go.th or the Thai embassy in ${nat.countryEn} with 500,000 THB bank proof.` },
      { q: `Do ${nat.demonymEn} citizens need to enter Thailand first?`, a: nat.visaExempt > 0 ? `Not required — enter visa-exempt for ${nat.visaExempt} days and file DTV in-country, or apply e-Visa from abroad.` : `Recommended to file e-Visa before travel; ${nat.demonymEn} passport holders don't receive visa-exempt entry.` },
      { q: `What language must documents be in?`, a: "English, or Thai-translated and MFA-certified. NYC handles translation + certification in one package." },
      { q: `Do I owe Thai tax on DTV?`, a: "Staying > 180 days/year makes you a Thai tax resident. File PND.90/91 on income remitted to Thailand." },
      { q: `What is NYC's total price?`, a: `Service THB ${cat.servicePriceTHB.toLocaleString()} + govt fee THB 10,000 = THB ${(cat.servicePriceTHB + 10000).toLocaleString()} total.` },
      { q: `How long does it take?`, a: `${cat.processingDays}. Express +THB 3,500 reduces to 3 business days.` },
    ];
    return {
      meta: [
        ...(isIndexable(url) ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { title: ctaTitle(title, "/en/dtv/$category/for/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/dtv/$category/for/$nationality", "en")},
        { name: "keywords", content: `DTV ${nat.demonymEn}, Destination Thailand Visa ${nat.countryEn}, ${cat.titleEn} ${nat.demonymEn}, Thailand digital nomad ${nat.demonymEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/dtv/$category/for/$nationality", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/dtv/$category/for/$nationality", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/dtv/${cat.slug}/for/${nat.slug}`, `/en/dtv/${cat.slug}/for/${nat.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${cat.titleEn} for ${nat.demonymEn} Citizens`,
              serviceType: cat.titleEn, description: desc, inLanguage: "en",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: nat.countryEn }],
              offers: { "@type": "Offer", price: String(cat.servicePriceTHB), priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          howToSchema({
            name: `How to apply for ${cat.titleEn} as a ${nat.demonymEn} citizen`,
            description: `${cat.taglineEn} — guide for ${nat.countryEn} passport holders`,
            totalTime: "P15D",
            estimatedCost: { currency: "THB", value: String(cat.servicePriceTHB + 10000) },
            inLanguage: "en",
            steps: cat.stepsEn,
          }),
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "DTV", item: `${SITE}/en/dtv` },
                { "@type": "ListItem", position: 3, name: cat.titleEn, item: `${SITE}/en/dtv/${cat.slug}` },
                { "@type": "ListItem", position: 4, name: nat.countryEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnDtvCategoryNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Not found</h1>
      <Link to="/en/dtv" className="mt-4 inline-block text-amber-300 underline">Back to DTV</Link>
    </main>
  ),
});

function EnDtvCategoryNationalityPage() {
  const { cat, nat } = Route.useLoaderData();
  const otherNats = VISA_NATIONALITIES.filter((n) => n.slug !== nat.slug).slice(0, 12);
  const otherCats = DTV_CATEGORIES.filter((c) => c.slug !== cat.slug).slice(0, 6);
  const faqs = [
    { q: `Can ${nat.demonymEn} citizens apply for ${cat.titleEn}?`, a: `Yes — open to all nationalities. File e-Visa at thaievisa.go.th or the Thai embassy in ${nat.countryEn} with 500,000 THB bank proof.` },
    { q: `Do ${nat.demonymEn} citizens need to enter Thailand first?`, a: nat.visaExempt > 0 ? `Not required — enter visa-exempt ${nat.visaExempt} days and file in-country.` : `Recommended to file e-Visa before travel.` },
    { q: `What language must documents be in?`, a: "English or Thai (MFA-certified). NYC handles both." },
    { q: `Do I owe Thai tax?`, a: "Staying > 180 days/year = tax resident; file PND.90/91 on income remitted to Thailand." },
    { q: `Total NYC price?`, a: `THB ${cat.servicePriceTHB.toLocaleString()} + govt THB 10,000 = THB ${(cat.servicePriceTHB + 10000).toLocaleString()}.` },
    { q: `Turnaround?`, a: `${cat.processingDays}. Express +THB 3,500 → 3 business days.` },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/dtv" className="hover:text-gold">DTV</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/dtv/$category" params={{ category: cat.slug }} className="hover:text-gold">{cat.titleEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{nat.flag} {nat.countryEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> {cat.icon} DTV · {nat.demonymEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {cat.titleEn}<br />for <span className="text-gold">{nat.demonymEn}</span> Citizens
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {cat.taglineEn} — guide for {nat.countryEn} passport holders: {nat.visaExempt ? `enter visa-exempt ${nat.visaExempt} days then file in-country` : "pre-arrival e-Visa required"}. Govt fee THB 10,000 + NYC service THB {cat.servicePriceTHB.toLocaleString()}, processed in {cat.processingDays}.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Total fee", v: `THB ${(cat.servicePriceTHB + 10000).toLocaleString()}` },
              { k: "Turnaround", v: cat.processingDays },
              { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} days` : "None" },
              { k: "Rating", v: "4.9 / 1,247" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC helps {nat.countryEn} passport holders file {cat.titleEn} — document prep, MFA-certified translation, and e-Visa submission at thaievisa.go.th — completed in {cat.processingDays} from THB {cat.servicePriceTHB.toLocaleString()} (plus government fee of THB 10,000). Ideal for {cat.whoEn}.
        </blockquote>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <DirectAnswer entity={`DTV for ${nat.countryEn}`}>
            {cat.quickAnswerEn} For {nat.demonymEn} citizens specifically: {nat.notes}
          </DirectAnswer>
          <KeyFactsTable rows={[
            { k: "Nationality", v: `${nat.flag} ${nat.countryEn}` },
            { k: "DTV category", v: cat.titleEn },
            { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} days` : "None" },
            { k: "Govt fee", v: "THB 10,000" },
            { k: "NYC service", v: `THB ${cat.servicePriceTHB.toLocaleString()}` },
            { k: "Turnaround", v: cat.processingDays },
          ]} />
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Why {nat.demonymEn} citizens choose {cat.titleEn}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            {nat.notes} — making {cat.titleEn} the optimal long-stay route for {nat.demonymEn} who don't want to open a company or buy property. DTV grants 5-year multiple entry, 180 days per stay (extendable +180 = 360 days/year). Best fit: {cat.whoEn}.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            What sets NYC apart: we are a Notarial Services Attorney registered with the Lawyers Council of Thailand and run MFA-certified translation in-house. {nat.demonymEn} clients send documents over LINE/email — no travel needed. We handle translation, certification, and e-Visa submission until approval lands within {cat.processingDays}.
          </p>
        </section>

        <section className="mt-12 grid gap-5 sm:grid-cols-2">
          {[
            { icon: FileText, title: "Core documents", items: cat.docsEn as string[] },
            { icon: ShieldCheck, title: "Sponsor / proof", items: [cat.sponsorEn, cat.whoEn] as string[] },
            { icon: Clock, title: "Timeline", items: cat.stepsEn.map((s: { name: string; text: string }) => `${s.name} — ${s.text.slice(0, 60)}…`) },
            { icon: Wallet, title: "All-in cost", items: [`NYC service THB ${cat.servicePriceTHB.toLocaleString()}`, "Government fee THB 10,000", "Notary +THB 1,500/doc (if required)", "Express 3-day +THB 3,500"] as string[] },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2"><b.icon className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-extrabold text-navy-deep">{b.title}</h3></div>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {b.items.slice(0, 6).map((it: string, i: number) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{it}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Package for {nat.countryEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">From THB {cat.servicePriceTHB.toLocaleString()} — end-to-end</h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Eligibility check + {cat.docsEn.length}-item document set</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> English translation + MFA certification</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> e-Visa submission at thaievisa.go.th + tracking</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3-day (+THB 3,500)</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other DTV categories for {nat.demonymEn} citizens</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherCats.map((c) => (
              <Link key={c.slug} to="/en/dtv/$category/for/$nationality" params={{ category: c.slug, nationality: nat.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span>{c.icon}</span>
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{c.titleEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{cat.titleEn} for other nationalities</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((n) => (
              <Link key={n.slug} to="/en/dtv/$category/for/$nationality" params={{ category: cat.slug, nationality: n.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{n.flag} {n.countryEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
